import { MetadataForQuery, AdminMemberEmailReq } from '../../../shared/dto/ReqDtoRepository';
import { AdminMemberResponse, AdminMemberListResponse, AdminOverallResponse } from '../../../shared/dto/ResDtoRepository';
import { useApiWrapper } from '../../../shared/hooks/useApi/apiWrapper';
import { convertMemberToGroup, getMember, getMemberList, getOverall, sendMemberEmail } from '../api/adminApi';
import { MemberData, OverallData } from '../../../shared/dto/EntityRepository';
import { MemberType } from '../../../shared/enum/EnumRepository';
import { usePageStore } from '../../../shared/store/pageStore';
import { AdminMemberEmailTarget, useAdminMemberListStore, useAdminMemberViewStore, useAdminStore } from '../store/adminMemberStore';
import { useTempAlertStore } from '../../../shared/store/portal/tempAlertStore';

interface UseMemberResult {
  overall: OverallData;
  activeMember: MemberData | null;
  isEmailViewerOpen: boolean;
  selectedEmailMembers: AdminMemberEmailTarget[];
  setActiveMember: (member: MemberData | null) => void;
  openEmailViewer: (members: AdminMemberEmailTarget[]) => void;
  closeEmailViewer: () => void;
  setSelectedEmailMembers: (members: AdminMemberEmailTarget[]) => void;
  memberList: MemberData[];
  getMember: (aui: string) => Promise<void>;
  getMemberList: (aui: string, metaData: MetadataForQuery) => Promise<void>;
  getOverall: (aui: string) => Promise<void>;
  sendMemberEmail: (data: AdminMemberEmailReq) => Promise<boolean>;
  convertMemberToGroup: (memberId: string) => Promise<boolean>;
}

export const useAdminMember = (): UseMemberResult => {
  const { overall, setOverall } = useAdminStore();
  const {
    activeMember,
    isEmailViewerOpen,
    selectedEmailMembers,
    setActiveMember,
    openEmailViewer,
    closeEmailViewer,
    setSelectedEmailMembers,
  } = useAdminMemberViewStore();
  const { memberList, setMemberList } = useAdminMemberListStore();
  const { setPage } = usePageStore();
  const withApiHandler = useApiWrapper();
  const { setUpdatedTempAlert } = useTempAlertStore();

  const handleGetMemberSuccess = (response: AdminMemberResponse) => {
    setActiveMember(response.data);
  };

  const handleGetMemberListSuccess = (response: AdminMemberListResponse) => {
    const data = response.data;
    setPage(data);
    setMemberList(data.content);
  };

  const handleGetOverallSuccess = (response: AdminOverallResponse) => {
    const data = response.data;
    setOverall(data);
  };

  const handleMemberRequest = async (
    aui: string,
    action: 'get' | 'get_list' | 'get_overall',
    metaData?: MetadataForQuery
  ) => {
    const apiFunction = async () => {
      switch (action) {
        case 'get_overall':
          handleGetOverallSuccess(await getOverall());
          break;
        case 'get':
          handleGetMemberSuccess(await getMember(aui));
          break;
        case 'get_list':
        default:
          handleGetMemberListSuccess(await getMemberList(metaData!));
          break;
      }
    };

    await withApiHandler(apiFunction, [aui, action, metaData]);
  };

  const getMemberHandler = (aui: string) => handleMemberRequest(aui, 'get');
  const getMemberListHandler = (aui: string, metaData: MetadataForQuery) => handleMemberRequest(aui, 'get_list', metaData);
  const getOverallHandler = (aui: string) => handleMemberRequest(aui, 'get_overall');
  const sendMemberEmailHandler = async (data: AdminMemberEmailReq): Promise<boolean> => {
    const response = await withApiHandler(async () => sendMemberEmail(data), [data]);
    if (!response) return false;
    setUpdatedTempAlert();
    return true;
  };

  const convertMemberToGroupHandler = async (memberId: string): Promise<boolean> => {
    const response = await withApiHandler(async () => convertMemberToGroup(memberId), [memberId]);
    if (!response) return false;
    setActiveMember(activeMember?.memberId === memberId ? { ...activeMember, memberType: MemberType.GROUP } : activeMember);
    setMemberList(memberList.map((member) =>
      member.memberId === memberId ? { ...member, memberType: MemberType.GROUP } : member
    ));
    setUpdatedTempAlert();
    return true;
  };

  return {
    overall,
    activeMember,
    isEmailViewerOpen,
    selectedEmailMembers,
    setActiveMember,
    openEmailViewer,
    closeEmailViewer,
    setSelectedEmailMembers,
    memberList,
    getMember: getMemberHandler,
    getMemberList: getMemberListHandler,
    getOverall: getOverallHandler,
    sendMemberEmail: sendMemberEmailHandler,
    convertMemberToGroup: convertMemberToGroupHandler,
  };
};
